import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Building2, Clock, FileCheck, DollarSign, Zap, ShieldCheck } from 'lucide-react';

const JumboTermLoan = () => {
  return (
    <div className="bg-[#FAFAFA]">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/1486222/pexels-photo-1486222.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-900/85 to-slate-900/70" />
        </div>
        <div className="absolute inset-0 noise-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 w-full py-28">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 mb-8">
              <Building2 className="h-4 w-4 text-sky-300" />
              <span className="text-xs font-semibold text-sky-200 uppercase tracking-wider">Jumbo Term Loan</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Jumbo<br /><span className="text-sky-300">Term Loan</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-xl">
              Bridge financing up to $25M designed to move fast. Close and fund in as little as 1 day with minimal documentation. Built for businesses that need capital now, not next quarter.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/apply" data-testid="jumbo-apply" className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-slate-100 transition-all">
                Apply Now <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-white/10 transition-all">Talk to Us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Stats Strip */}
      <section className="bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: <DollarSign className="h-5 w-5 text-sky-400" />, value: 'Up to $25M', label: 'Loan Amount' },
              { icon: <Clock className="h-5 w-5 text-sky-400" />, value: '1 Day', label: 'Close and Fund' },
              { icon: <FileCheck className="h-5 w-5 text-sky-400" />, value: 'Low Doc', label: 'Streamlined Process' },
              { icon: <Zap className="h-5 w-5 text-sky-400" />, value: 'Bridge', label: 'Loan Structure' },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  {stat.icon}
                </div>
                <div>
                  <div className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>{stat.value}</div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Details Grid */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Program Details</h2>
              <p className="text-slate-500 mb-10">The Jumbo Term Loan is a bridge financing solution built for speed and simplicity. Designed for businesses that need significant capital on a compressed timeline with minimal paperwork.</p>
              {[
                { label: 'Loan Amount', value: 'Up to $25,000,000' },
                { label: 'Loan Type', value: 'Bridge / Term Loan' },
                { label: 'Closing Timeline', value: 'As fast as 1 business day' },
                { label: 'Documentation', value: 'Low doc / streamlined' },
                { label: 'Funding Speed', value: 'Same day as closing' },
              ].map((item) => (
                <div key={item.label} className="flex justify-between items-center py-4 border-b border-slate-200">
                  <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{item.label}</span>
                  <span className="text-sm font-bold text-slate-900">{item.value}</span>
                </div>
              ))}
            </div>

            <div className="space-y-8">
              {/* Image card */}
              <div className="relative rounded-2xl overflow-hidden h-56">
                <img src="https://images.pexels.com/photos/3183183/pexels-photo-3183183.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Business meeting" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>Speed When It Matters</div>
                  <p className="text-xs text-sky-100/80 mt-1">Close and fund in as little as 1 business day. No long waiting periods, no unnecessary delays.</p>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Ideal Use Cases</h3>
                {[
                  'Bridge financing for acquisitions or real estate',
                  'Time sensitive business opportunities',
                  'Large scale working capital needs',
                  'Refinancing existing obligations',
                  'Partnership buyouts',
                  'Rapid expansion or market entry',
                ].map((use) => (
                  <div key={use} className="flex items-center gap-3 mb-4 last:mb-0">
                    <CheckCircle className="h-5 w-5 text-sky-600 flex-shrink-0" />
                    <span className="text-sm text-slate-600">{use}</span>
                  </div>
                ))}
              </div>

              <Link to="/apply" className="block bg-sky-600 text-white rounded-2xl p-8 group hover:bg-sky-500 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Get Started Today</h3>
                    <p className="text-sm text-sky-200">Close in as little as 1 day</p>
                  </div>
                  <ArrowRight className="h-6 w-6 text-white group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Jumbo */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Why the Jumbo Term Loan</h2>
            <p className="text-slate-500 max-w-xl mx-auto">When the deal won't wait, you need a financing partner that moves as fast as you do.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: <Clock className="h-6 w-6" />, title: '1 Day Closing', desc: 'From application to funded in as little as 1 business day. Speed is built into every step of the process.', color: 'bg-sky-50 text-sky-700' },
              { icon: <FileCheck className="h-6 w-6" />, title: 'Low Documentation', desc: 'Streamlined paperwork designed for experienced business owners. No unnecessary hoops to jump through.', color: 'bg-emerald-50 text-emerald-700' },
              { icon: <ShieldCheck className="h-6 w-6" />, title: 'Up to $25M', desc: 'Significant capital for significant opportunities. Bridge the gap between where you are and where you need to be.', color: 'bg-purple-50 text-purple-700' },
            ].map((card) => (
              <div key={card.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
                <div className={`w-12 h-12 rounded-xl ${card.color} flex items-center justify-center mb-6`}>
                  {card.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{card.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/85" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Ready to Move Fast?</h2>
          <p className="text-lg text-slate-400 mb-10 max-w-xl mx-auto">The Jumbo Term Loan is built for businesses that need significant capital on a compressed timeline. Let's talk.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/apply" className="inline-flex items-center gap-2 bg-sky-600 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-sky-500 transition-colors">Apply Now <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/10 transition-colors">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JumboTermLoan;
